import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Button, IconButton, MenuItem, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, CircularProgress, Tooltip
} from '@mui/material';
import { Visibility, Paid, Cancel, Refresh, Download, AddCircleOutline } from '@mui/icons-material';
import api from '../../services/api';
import { brand } from '../../theme/colors';

const STATUS_COLORS = { pending: 'warning', processing: 'info', completed: 'success', failed: 'error', cancelled: 'default' };
const ROLES = ['contractor', 'verifier', 'quality_inspector', 'project_manager'];
const METHODS = ['bank_transfer', 'ecocash', 'onemoney', 'mukuru', 'worldremit'];

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const summariseDetails = (pd) => {
  if (!pd) return '—';
  const d = typeof pd === 'string' ? (() => { try { return JSON.parse(pd); } catch { return {}; } })() : pd;
  return [d.account_name, d.account_number, d.bank_name, d.phone, d.recipient_name, d.country]
    .filter(Boolean).join(' · ') || '—';
};

const AdminWithdrawals = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [roleFilter, setRoleFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [detail, setDetail] = useState(null);
  const [payDialog, setPayDialog] = useState(null); // row being marked paid
  const [rejectDialog, setRejectDialog] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  const [payForm, setPayForm] = useState({ transactionReference: '', paidAt: new Date().toISOString().slice(0, 10), notes: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [manualForm, setManualForm] = useState({ userId: '', userRole: 'contractor', amount: '', paymentMethod: 'bank_transfer', transactionReference: '', notes: '', account_name: '', account_number: '', bank_name: '' });
  const [busy, setBusy] = useState(false);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (roleFilter) params.set('userRole', roleFilter);
      params.set('limit', '100');
      const res = await api.get(`/payments/admin/withdrawals?${params.toString()}`);
      setRows(res.data?.data || []);
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to load withdrawals', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/payments/admin/withdrawals/${id}`);
      setDetail(res.data?.data);
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to load detail', 'error');
    }
  };

  const submitMarkPaid = async () => {
    if (!payForm.transactionReference.trim()) { notify('Transaction reference is required', 'warning'); return; }
    setBusy(true);
    try {
      await api.post(`/payments/admin/withdrawals/${payDialog.id}/mark-paid`, {
        transactionReference: payForm.transactionReference.trim(),
        paidAt: payForm.paidAt ? new Date(payForm.paidAt).toISOString() : undefined,
        notes: payForm.notes
      });
      notify('Marked as paid');
      setPayDialog(null);
      setPayForm({ transactionReference: '', paidAt: new Date().toISOString().slice(0, 10), notes: '' });
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to mark paid', 'error');
    } finally { setBusy(false); }
  };

  const submitReject = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/admin/withdrawals/${rejectDialog.id}/reject`, { reason: rejectReason });
      notify('Withdrawal rejected');
      setRejectDialog(null);
      setRejectReason('');
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to reject', 'error');
    } finally { setBusy(false); }
  };

  const submitManual = async () => {
    if (!manualForm.userId.trim() || !manualForm.amount || !manualForm.transactionReference.trim()) {
      notify('User ID, amount, and reference are required', 'warning'); return;
    }
    setBusy(true);
    try {
      await api.post('/payments/admin/withdrawals/manual', {
        userId: manualForm.userId.trim(),
        userRole: manualForm.userRole,
        amount: parseFloat(manualForm.amount),
        paymentMethod: manualForm.paymentMethod,
        paymentDetails: { account_name: manualForm.account_name, account_number: manualForm.account_number, bank_name: manualForm.bank_name },
        transactionReference: manualForm.transactionReference.trim(),
        notes: manualForm.notes
      });
      notify('Manual disbursement recorded');
      setManualOpen(false);
      setManualForm({ userId: '', userRole: 'contractor', amount: '', paymentMethod: 'bank_transfer', transactionReference: '', notes: '', account_name: '', account_number: '', bank_name: '' });
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to record disbursement', 'error');
    } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = ['id', 'requester', 'email', 'role', 'amount', 'currency', 'method', 'destination', 'status', 'reference', 'processed_at'];
    const lines = rows.map((r) => [
      r.id, r.full_name || '', r.email || '', r.user_role, r.amount, r.currency, r.payment_method,
      summariseDetails(r.payment_details), r.status, r.transaction_id || '', r.processed_at || ''
    ].map(esc).join(','));
    const blob = new Blob([[headers.map(esc).join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `withdrawals-${statusFilter || 'all'}-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {['pending', 'processing', 'completed', 'failed'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">All roles</MenuItem>
          {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </TextField>
        <Button startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Button startIcon={<Download />} onClick={exportCsv} disabled={!rows.length}>Export CSV</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => setManualOpen(true)}
          sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}>
          Record manual disbursement
        </Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Requester</TableCell><TableCell>Role</TableCell><TableCell align="right">Amount</TableCell>
                    <TableCell>Method</TableCell><TableCell>Destination</TableCell><TableCell>Status</TableCell>
                    <TableCell>Requested</TableCell><TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No withdrawals found</TableCell></TableRow>
                  ) : rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell><strong>{r.full_name || '—'}</strong><br /><Typography variant="caption" color="text.secondary">{r.email}</Typography></TableCell>
                      <TableCell>{r.user_role}</TableCell>
                      <TableCell align="right">{fmt(r.amount)}</TableCell>
                      <TableCell>{r.payment_method}</TableCell>
                      <TableCell><Typography variant="caption">{summariseDetails(r.payment_details)}</Typography></TableCell>
                      <TableCell><Chip size="small" label={r.status} color={STATUS_COLORS[r.status] || 'default'} /></TableCell>
                      <TableCell>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View"><IconButton size="small" onClick={() => openDetail(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                        {r.status === 'pending' && (
                          <>
                            <Tooltip title="Mark as paid"><IconButton size="small" color="success" onClick={() => setPayDialog(r)}><Paid fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => setRejectDialog(r)}><Cancel fontSize="small" /></IconButton></Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Withdrawal Detail</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 1 }}>
              <Typography color="text.secondary">Requester</Typography><Typography>{detail.withdrawal.full_name} ({detail.withdrawal.email})</Typography>
              <Typography color="text.secondary">Amount</Typography><Typography>{fmt(detail.withdrawal.amount)} {detail.withdrawal.currency}</Typography>
              <Typography color="text.secondary">Method</Typography><Typography>{detail.withdrawal.payment_method}</Typography>
              <Typography color="text.secondary">Destination</Typography><Typography>{summariseDetails(detail.withdrawal.payment_details)}</Typography>
              <Typography color="text.secondary">Status</Typography><Typography>{detail.withdrawal.status}</Typography>
              <Typography color="text.secondary">Available balance</Typography><Typography>{fmt(detail.userBalance?.available)}</Typography>
              <Typography color="text.secondary">Reference</Typography><Typography>{detail.withdrawal.transaction_id || '—'}</Typography>
              <Typography color="text.secondary">Notes</Typography><Typography>{detail.withdrawal.processing_notes || '—'}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Mark paid dialog */}
      <Dialog open={!!payDialog} onClose={() => setPayDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Paid</DialogTitle>
        <DialogContent dividers>
          {payDialog && <Alert severity="info" sx={{ mb: 2 }}>Recording manual payout of {fmt(payDialog.amount)} to {payDialog.full_name}.</Alert>}
          <TextField fullWidth label="Transaction reference" required value={payForm.transactionReference}
            onChange={(e) => setPayForm((p) => ({ ...p, transactionReference: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label="Paid on" type="date" value={payForm.paidAt}
            onChange={(e) => setPayForm((p) => ({ ...p, paidAt: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <TextField fullWidth label="Notes" multiline rows={2} value={payForm.notes}
            onChange={(e) => setPayForm((p) => ({ ...p, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={busy} onClick={submitMarkPaid}>Confirm paid</Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Withdrawal</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth label="Reason" multiline rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy} onClick={submitReject}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Manual disbursement dialog */}
      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Manual Disbursement</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>Use this to record a payout you made off-platform to a user who did not submit a request. It will reduce their available balance.</Alert>
          <TextField fullWidth label="User ID (UUID)" required value={manualForm.userId} onChange={(e) => setManualForm((p) => ({ ...p, userId: e.target.value }))} sx={{ mb: 2 }} />
          <TextField select fullWidth label="Role" value={manualForm.userRole} onChange={(e) => setManualForm((p) => ({ ...p, userRole: e.target.value }))} sx={{ mb: 2 }}>
            {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Amount (USD)" type="number" required value={manualForm.amount} onChange={(e) => setManualForm((p) => ({ ...p, amount: e.target.value }))} sx={{ mb: 2 }} />
          <TextField select fullWidth label="Method" value={manualForm.paymentMethod} onChange={(e) => setManualForm((p) => ({ ...p, paymentMethod: e.target.value }))} sx={{ mb: 2 }}>
            {METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Account name" value={manualForm.account_name} onChange={(e) => setManualForm((p) => ({ ...p, account_name: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label="Account number / phone" value={manualForm.account_number} onChange={(e) => setManualForm((p) => ({ ...p, account_number: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label="Bank / provider" value={manualForm.bank_name} onChange={(e) => setManualForm((p) => ({ ...p, bank_name: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label="Transaction reference" required value={manualForm.transactionReference} onChange={(e) => setManualForm((p) => ({ ...p, transactionReference: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label="Notes" multiline rows={2} value={manualForm.notes} onChange={(e) => setManualForm((p) => ({ ...p, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={submitManual} sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}>Record</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminWithdrawals;
